/** The app's "More" tab: your waivers, your check-in code, and the club. */
import { useQuery } from '@tanstack/react-query';
import { dateTime, DAYS, type League } from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, Empty, ErrorNote, Loading, Pill, Status } from '../components/ui';
import { PageHeader } from '../App';

interface MyWaivers {
  isCleared: boolean;
  outstanding: Array<{ code: string; title: string; version: string }>;
  signed: Array<{ id: string; title: string; version: string; state: string; signedAt: string | null }>;
}

export function MePage() {
  const { user, club } = useAuth();

  const waivers = useQuery({
    queryKey: ['waivers', 'mine'],
    queryFn: () => api.get<{ data: MyWaivers }>('/waivers/mine').then((r) => r.data),
  });
  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.get<{ data: League[] }>('/leagues').then((r) => r.data),
  });

  return (
    <>
      <PageHeader title="You" sub="Your waivers, and the club's details" />
      <div className="content stack">
        <Card title="Your account">
          <dl className="mv-facts">
            <div><dt>Name</dt><dd>{user?.fullName}</dd></div>
            <div><dt>Email</dt><dd>{user?.email}</dd></div>
            <div><dt>Club</dt><dd>{club?.name ?? '—'}</dd></div>
          </dl>
        </Card>

        <Card title="Waivers">
          {waivers.isLoading && <Loading />}
          {waivers.error && <ErrorNote error={waivers.error} />}
          {waivers.data && (
            <>
              <p style={{ marginTop: 0 }}>
                {waivers.data.isCleared
                  ? <Pill tone="good">All signed — cleared to play</Pill>
                  : <Pill tone="warn">{waivers.data.outstanding.length} still to sign</Pill>}
              </p>

              {waivers.data.outstanding.length > 0 && (
                <>
                  <p className="small">
                    A team cannot be drawn with an outstanding waiver against it, so this is
                    worth clearing before your first draw.
                  </p>
                  <div className="table-wrap">
                    <table className="t">
                      <caption className="sr-only">Waivers you still have to sign</caption>
                      <tbody>
                        {waivers.data.outstanding.map((w) => (
                          <tr key={w.code}>
                            <td>{w.title}</td>
                            <td className="muted small">version {w.version}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {club?.publicSiteUrl && (
                    <p style={{ marginBottom: 0 }}>
                      <a className="btn btn--primary btn--sm"
                        href={`${club.publicSiteUrl.replace(/\/+$/, '')}/sign-waiver.html`}
                        target="_blank" rel="noopener">
                        Sign them now
                      </a>
                    </p>
                  )}
                </>
              )}

              {waivers.data.signed.length > 0 && (
                <>
                  <h3 style={{ marginTop: '1.25rem' }}>Signed</h3>
                  <div className="table-wrap">
                    <table className="t">
                      <caption className="sr-only">Waivers you have signed</caption>
                      <thead><tr><th>Waiver</th><th>Version</th><th>Signed</th><th>State</th></tr></thead>
                      <tbody>
                        {waivers.data.signed.map((w) => (
                          <tr key={w.id}>
                            <td>{w.title}</td>
                            <td className="muted small">{w.version}</td>
                            <td className="nowrap small">
                              {w.signedAt ? dateTime(w.signedAt) : '—'}
                            </td>
                            <td><Status value={w.state} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Card>

        <Card title="Checking in">
          <p style={{ marginTop: 0 }}>
            Checking in is done on the phone app: it shows a code that changes every few
            seconds, which the desk scans or reads. There is deliberately no code here &mdash;
            a check-in code on a screen you can leave open is a code somebody else can use.
          </p>
          <p className="small muted" style={{ marginBottom: 0 }}>
            Open the {club?.name ?? 'club'} app and tap <b>Check in at the club</b>.
          </p>
        </Card>

        <Card title={club?.name ?? 'The club'}>
          <dl className="mv-facts">
            {club?.phone && (
              <div>
                <dt>Phone</dt>
                <dd><a className="link" href={`tel:${club.phone.replace(/[^\d+]/g, '')}`}>{club.phone}</a></dd>
              </div>
            )}
            {club?.email && (
              <div>
                <dt>Email</dt>
                <dd><a className="link" href={`mailto:${club.email}`}>{club.email}</a></dd>
              </div>
            )}
            {club?.address && <div><dt>Address</dt><dd>{club.address}</dd></div>}
            {club?.publicSiteUrl && (
              <div>
                <dt>Website</dt>
                <dd>
                  <a className="link" href={club.publicSiteUrl} target="_blank" rel="noopener">
                    {club.publicSiteUrl.replace(/^https?:\/\//, '')}
                  </a>
                </dd>
              </div>
            )}
            <div><dt>Sheets of ice</dt><dd>{club?.sheets ?? '—'}</dd></div>
          </dl>
        </Card>

        <Card title="Leagues &amp; draw times">
          {leagues.isLoading && <Loading />}
          {leagues.data && leagues.data.length === 0 && <Empty title="No leagues yet" />}
          {leagues.data && leagues.data.length > 0 && (
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Every league and its draw times</caption>
                <thead><tr><th>League</th><th>Draw times</th><th>Convenor</th></tr></thead>
                <tbody>
                  {leagues.data.map((l) => (
                    <tr key={l.id}>
                      <td>{l.name}</td>
                      <td className="small">
                        {l.drawTimes.length
                          ? l.drawTimes
                              .map((d) => `${DAYS[d.dayOfWeek]?.slice(0, 3) ?? ''} ${d.startTime}`)
                              .join(' · ')
                          : 'To be confirmed'}
                      </td>
                      <td className="small muted">
                        {l.conveners.map((x) => x.fullName).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
